import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { capabilitiesFor, type Capability, type Surface } from '@/config/surfacePolicy';
import {
  readSurfaceOverride,
  resolveSurface,
  surfaceDisagreement,
  type SurfaceResolution,
  type SurfaceSource,
} from '@/config/surfaceDetection';

export type { Capability, Surface } from '@/config/surfacePolicy';
export type { SurfaceSource } from '@/config/surfaceDetection';

export interface SurfaceValue {
  /** kiosk | mobile (every non-kiosk deployment) | unknown (no flag, server not answered yet). */
  surface: Surface;
  isKiosk: boolean;
  /** Running as the installed PWA (`display-mode: standalone`, or iOS `navigator.standalone`). */
  isInstalledApp: boolean;
  /** The §2 table (src/config/surfacePolicy.ts) for this surface. */
  can: (capability: Capability) => boolean;
  /** What decided the surface; DeviceModeContext lets a `?surface=kiosk` override give the kiosk class on a preview host. */
  source: SurfaceSource;
  /**
   * Unknown, and /api/kiosk/info is still being asked: the first answer or the short retries
   * (5 s, 15 s, 30 s) are pending. False once the surface is known, the server gave a real answer,
   * or the retries fall back to every 60 s (unknown for now). /play waits on it.
   */
  detecting: boolean;
}

const SurfaceContext = createContext<SurfaceValue | null>(null);

const STANDALONE_QUERY = '(display-mode: standalone)';

/**
 * The detection inputs. main.tsx builds them from the build env and the kiosk info cache
 * (src/config/surfaceEnv.ts surfaceEnvironment()); passing them in keeps this module free of
 * `import.meta` and the API client, so any component test can render under a SurfaceProvider.
 */
export interface SurfaceEnvironment {
  /** VITE_DOMAIN_TYPE (or the dev VITE_DEV_DOMAIN_TYPE). */
  flag?: string;
  /** DEV / VITE_UI_PREVIEW: the page-load `?surface=kiosk|mobile` is honoured (and final). */
  overrideAllowed?: boolean;
  /** The last cached /api/kiosk/info type for this host (synchronous, for the first frame). */
  readCachedType?: () => string | undefined;
  /** GET /api/kiosk/info type (fetchKioskInfo: on failure it answers the cached copy, else null). */
  fetchServerType?: () => Promise<string | null | undefined>;
  /** Inside the native kiosk shell: always the kiosk surface, and /api/kiosk/info is not asked. */
  nativeHost?: boolean;
}

function readSearch(): string {
  return globalThis.location?.search ?? '';
}

function readOverride(env: SurfaceEnvironment) {
  return readSurfaceOverride(readSearch(), env.overrideAllowed === true);
}

/**
 * The surface before /api/kiosk/info answers: the dev/preview `?surface=`, else kiosk when the
 * cached server answer for this host or the build flag says kiosk, else the cached answer, else
 * the flag, else unknown (resolveSurface). Synchronous, for the first paint
 * (main.tsx bootDeviceMode and the provider's first render).
 */
export function readInitialSurface(env: SurfaceEnvironment): SurfaceResolution {
  let cached: string | undefined;
  try {
    cached = env.readCachedType?.();
  } catch {
    cached = undefined;
  }
  return resolveSurface({ host: env.nativeHost, override: readOverride(env), server: cached, flag: env.flag });
}

function readInstalledApp(): boolean {
  try {
    if (typeof window.matchMedia === 'function' && window.matchMedia(STANDALONE_QUERY).matches) return true;
  } catch {
    /* no matchMedia */
  }
  return (globalThis.navigator as Navigator & { standalone?: boolean } | undefined)?.standalone === true;
}

/** One warning per flag/server pair for the page's life (no repeat on remounts or StrictMode). */
let warnedDisagreement: string | null = null;

function warnOnce(message: string | null): void {
  if (!message || message === warnedDisagreement) return;
  warnedDisagreement = message;
  console.warn(message);
}

/** Test hook: forget the warning already given. */
export function __resetSurfaceWarningForTests(): void {
  warnedDisagreement = null;
}

function sameResolution(a: SurfaceResolution, b: SurfaceResolution): boolean {
  return a.surface === b.surface && a.source === b.source;
}

interface SurfaceProviderProps {
  children: React.ReactNode;
  /** Detection inputs (main.tsx: surfaceEnvironment()). Without them: unknown, unless `surface` is set. */
  environment?: SurfaceEnvironment;
  /** A fixed surface with no detection (tests, the UI catalog). */
  surface?: Surface;
}

const NO_ENVIRONMENT: SurfaceEnvironment = {};

/**
 * While the surface is unknown because /api/kiosk/info failed (no flag, no cache), it is asked
 * again after these delays; the last one repeats until the surface is known. Coming back online
 * asks at once.
 */
export const SURFACE_RETRY_DELAYS_MS: readonly number[] = Object.freeze([5_000, 15_000, 30_000, 60_000]);

/** The steady retry (the last delay): from here on the surface counts as unknown, not detecting. */
const STEADY_RETRY_INDEX = SURFACE_RETRY_DELAYS_MS.length - 1;

/**
 * Kiosk / non-kiosk surface (spec 2026-10-08-kiosk-web-split §3): starts from readInitialSurface()
 * and asks /api/kiosk/info. Kiosk wins when either the server or the build flag says kiosk; else
 * a mobile answer wins over the flag (one console.warn when the two differ); any other answer, or
 * a failure, leaves the flag standing. When the fetch
 * fails and nothing else names the surface (no flag, no cache), it asks again
 * (SURFACE_RETRY_DELAYS_MS, and on the window's `online` event) until it is known. A dev/preview
 * `?surface=` is final. The environment is read once, at mount.
 */
export function SurfaceProvider({ children, environment = NO_ENVIRONMENT, surface: fixed }: SurfaceProviderProps) {
  const [env] = useState(environment);
  const [resolution, setResolution] = useState<SurfaceResolution>(() =>
    fixed ? { surface: fixed, source: 'override' } : readInitialSurface(env)
  );
  const [isInstalledApp, setInstalledApp] = useState(readInstalledApp);
  const [detecting, setDetecting] = useState(
    () => !fixed && resolution.source === 'none' && !!env.fetchServerType && !readOverride(env)
  );

  useEffect(() => {
    if (fixed) {
      setResolution((prev) => (prev.surface === fixed ? prev : { surface: fixed, source: 'override' }));
      setDetecting(false);
      return;
    }
    const fetchServerType = env.fetchServerType;
    if (env.nativeHost || readOverride(env) || !fetchServerType) return;
    let cancelled = false;
    let settled = false;
    let inFlight = false;
    let failures = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const stop = () => {
      settled = true;
      clearTimeout(timer);
      window.removeEventListener('online', ask);
      setDetecting(false);
    };

    function ask() {
      if (cancelled || settled || inFlight) return;
      inFlight = true;
      clearTimeout(timer);
      fetchServerType!()
        .then(
          (serverType) => serverType,
          () => null // the flag (or unknown) stands
        )
        .then((serverType) => {
          inFlight = false;
          if (cancelled) return;
          warnOnce(surfaceDisagreement(env.flag, serverType));
          const next = resolveSurface({ host: env.nativeHost, override: null, server: serverType, flag: env.flag });
          setResolution((prev) => (sameResolution(prev, next) ? prev : next));
          // Known (server, flag), or the server answered something else: nothing more to ask.
          if (next.source !== 'none' || serverType != null) {
            stop();
            return;
          }
          const index = Math.min(failures, STEADY_RETRY_INDEX);
          if (index === STEADY_RETRY_INDEX) setDetecting(false);
          const delay = SURFACE_RETRY_DELAYS_MS[index];
          failures += 1;
          timer = setTimeout(ask, delay);
        });
    }

    window.addEventListener('online', ask);
    ask();
    return () => {
      cancelled = true;
      clearTimeout(timer);
      window.removeEventListener('online', ask);
    };
  }, [fixed, env]);

  // Installing the PWA from the browser menu, or opening it, switches display-mode.
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    let mq: MediaQueryList;
    try {
      mq = window.matchMedia(STANDALONE_QUERY);
    } catch {
      return;
    }
    const sync = () => setInstalledApp(readInstalledApp());
    mq.addEventListener?.('change', sync);
    return () => mq.removeEventListener?.('change', sync);
  }, []);

  const { surface, source } = resolution;
  const can = useCallback((capability: Capability) => capabilitiesFor(surface)[capability] === true, [surface]);
  const value = useMemo<SurfaceValue>(
    () => ({ surface, isKiosk: surface === 'kiosk', isInstalledApp, can, source, detecting }),
    [surface, isInstalledApp, can, source, detecting]
  );
  return <SurfaceContext.Provider value={value}>{children}</SurfaceContext.Provider>;
}

const OUTSIDE_PROVIDER: SurfaceValue = Object.freeze({
  surface: 'unknown',
  isKiosk: false,
  isInstalledApp: false,
  can: () => false,
  source: 'none',
  detecting: false,
});

/**
 * The surface and its capabilities. Outside a provider (isolated renders, tests): unknown, with
 * every capability off.
 */
export function useSurface(): SurfaceValue {
  return useContext(SurfaceContext) ?? OUTSIDE_PROVIDER;
}
